import { Button, Card, Empty, Input, Space, Table, Tag, Typography, Alert, Modal } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { SearchOutlined } from '@ant-design/icons'
import { useEffect, useMemo, useState } from 'react'
import type { Key } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDeleteReportBuilderListMutation, useReportBuilderList } from '../../hooks/useReports'
import { useAuthSession } from '../../lib/session'
import { permissions, UserTypes } from '../../types/authorization'
import type { ReportBuilderListItem } from '../../types/reports'
import { useToast } from '../../contexts/ToastContext'
import { useAuthorization } from '../../contexts/AuthorizationContext'

export default function ReportsTemplatesPage() {
  const session = useAuthSession()
  const { hasAnyPermission } = useAuthorization()
  const navigate = useNavigate()
  const location = useLocation()
  const clientId = session?.currentClientId || ''
  const isDiAdmin = session?.currentUserTypeId === UserTypes.Admin
  const canCreateOrEditReports = hasAnyPermission([
    permissions.addReport,
    permissions.editReport,
    permissions.updateNotification,
  ])
  const canDeleteTemplates = isDiAdmin && canCreateOrEditReports
  const [titleFilter, setTitleFilter] = useState('')
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const { addToast } = useToast()

  const reportsQuery = useReportBuilderList(clientId, true, { title: titleFilter })
  const deleteSelectedMutation = useDeleteReportBuilderListMutation()
  const rows = reportsQuery.data || []

  useEffect(() => {
    setCurrentPage(1)
  }, [titleFilter])

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(rows.length / pageSize))
    if (currentPage > maxPage) {
      setCurrentPage(maxPage)
    }
  }, [currentPage, pageSize, rows.length])

  const columns = useMemo<ColumnsType<ReportBuilderListItem>>(
    () => [
      {
        title: 'Report Name',
        dataIndex: 'title',
        key: 'title',
        sorter: (a, b) => a.title.localeCompare(b.title),
        render: (_, row) => (
          <Button type="link" className="px-0" onClick={() => navigate(`/reports/custom/${row.reportBuilderId}`)}>
            {row.title}
          </Button>
        ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: (value: string) => value || '-',
      },
      {
        title: 'Type',
        key: 'isTemplate',
        width: 140,
        render: (_, row) => <Tag color={row.isTemplate ? 'gold' : 'blue'}>{row.isTemplate ? 'Template' : 'Custom'}</Tag>,
      },
    ],
    [navigate]
  )

  const handleDeleteSelected = () => {
    if (!selectedKeys.length) {
      addToast('No templates selected.', 'warning')
      return
    }

    Modal.confirm({
      title: 'Delete template reports',
      content: `Delete ${selectedKeys.length} selected template report(s)?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        const ids = selectedKeys.map((value) => String(value))
        const ok = await deleteSelectedMutation.mutateAsync(ids)
        if (ok) {
          addToast('Successfully deleted template reports.', 'success')
          setSelectedKeys([])
          await reportsQuery.refetch()
          return
        }
        addToast('Failed to delete template reports.', 'error')
      },
    })
  }

  return (
    <Card>
      <Space direction="vertical" size={16} className="w-full">
        <Space className="w-full justify-between" align="start">
          <div>
            <Typography.Title level={3} className="!mb-1">
              PracIntel Templates
            </Typography.Title>
            <Typography.Text type="secondary">Current client: {session?.currentClientName || 'Unknown'}</Typography.Text>
          </div>
          <Space wrap>
            <Button onClick={() => navigate(-1)}>Back</Button>
            <Button type={location.pathname === '/reports/created' ? 'primary' : 'default'} onClick={() => navigate('/reports/created')}>
              Created
            </Button>
            <Button type={location.pathname === '/reports/templates' ? 'primary' : 'default'} onClick={() => navigate('/reports/templates')}>
              Templates
            </Button>
            {isDiAdmin ? (
              <Button onClick={() => navigate('/reports/options')}>
                Options
              </Button>
            ) : null}
            {canCreateOrEditReports ? (
              <Button type="primary" onClick={() => navigate('/reports/custom/new')}>
                + New Report
              </Button>
            ) : null}
          </Space>
        </Space>

        {!clientId ? (
          <Alert type="warning" showIcon message="No active client selected." />
        ) : null}

        <Space className="w-full justify-between" wrap>
          <Input
            value={titleFilter}
            onChange={(event) => setTitleFilter(event.target.value)}
            placeholder="Search template reports"
            suffix={<SearchOutlined />}
            allowClear
            style={{ width: 320 }}
            aria-label="Search template reports"
          />
          <Space>
            {reportsQuery.isFetching && !reportsQuery.isLoading ? <Typography.Text type="secondary">Refreshing…</Typography.Text> : null}
            {canDeleteTemplates ? (
              <Button danger onClick={handleDeleteSelected} disabled={!selectedKeys.length || deleteSelectedMutation.isPending}>
                Delete Selected
              </Button>
            ) : null}
          </Space>
        </Space>

        {reportsQuery.isError ? (
          <Alert
            type="error"
            showIcon
            message="Failed to load report templates"
            description={reportsQuery.error instanceof Error ? reportsQuery.error.message : 'Unknown error'}
            action={
              <Button size="small" onClick={() => reportsQuery.refetch()}>
                Retry
              </Button>
            }
          />
        ) : null}

        {!reportsQuery.isError && !reportsQuery.isLoading && rows.length === 0 ? (
          <Empty description="No report templates found" />
        ) : null}

        <Table<ReportBuilderListItem>
          rowKey="reportBuilderId"
          loading={reportsQuery.isLoading}
          columns={columns}
          dataSource={rows}
          rowSelection={
            canDeleteTemplates
              ? {
                  selectedRowKeys: selectedKeys,
                  onChange: (keys) => setSelectedKeys(keys),
                }
              : undefined
          }
          pagination={{
            current: currentPage,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, nextPageSize) => {
              setCurrentPage(page)
              setPageSize(nextPageSize)
            },
          }}
        />
      </Space>
    </Card>
  )
}
